import * as THREE from "three"
import React, { useEffect, useRef, useState } from "react"
import { useFrame, useThree } from "react-three-fiber"
import { SkeletonUtils } from "three/examples/jsm/utils/SkeletonUtils"
import { useMemo } from "react"
import lerp from "lerp"
import { getMouseDegrees } from "../../utils"

function playModifierAnimation(from, fSpeed, to, tSpeed, isAnimating) {
  isAnimating.current = true
  to.setLoop(THREE.LoopOnce)
  to.reset()
  to.play()
  from.crossFadeTo(to, fSpeed, true)
  setTimeout(function() {
    from.enabled = true
    to.crossFadeTo(from, tSpeed, true)
    isAnimating.current = false
  }, to._clip.duration * 1000 - (tSpeed + fSpeed) * 1000)
}

export function ContactModelRenderer({ theme, ...props }) {
  const { scene } = useThree()
  const [, forceUpdate] = useState()
  useEffect(() => {
    // To make the scene appear as transparent
    // scene.background = new THREE.Color(0x2f2e2e)
    scene.background = new THREE.Color(theme === "light" ? 0xece8e9 : 0x2f2e2e)
    forceUpdate(true)
  }, [scene.background, theme])

  return <ModelRenderer {...props} />
}

function ModelRenderer({
  // nodes,
  texture,
  animations,
  isLinkHovered,
  scene: modelSceneObject,
  inView,
}) {
  const group = useRef()
  const { nodes } = useMemo(() => {
    let nodes = {},
      materials = {}
    const model = SkeletonUtils.clone(modelSceneObject)
    model.traverse(object => {
      if (object.name) {
        nodes = { ...nodes, [object.name]: object }
      }
      if (object.material && !materials[object.material.name]) {
        materials[object.material.name] = object.material
      }
    })
    return { model, nodes, materials }
  }, [])
  // modelScene.scale.set(0.25, 0.25, 0.25)
  // modelScene.position.y = -1
  // modelScene.position.z = 2
  const mixer = useMemo(() => new THREE.AnimationMixer(), [])
  const isAnimating = useRef(false)
  const actions = useRef()

  useEffect(() => {
    actions.current = {
      idle: mixer.clipAction(
        THREE.AnimationClip.findByName(animations, "idleStandard"),
        group.current
      ),
      wave: mixer.clipAction(
        THREE.AnimationClip.findByName(animations, "wave"),
        group.current
      ),
      landing: mixer.clipAction(
        THREE.AnimationClip.findByName(animations, "landing"),
        group.current
      ),
      looking: mixer.clipAction(
        THREE.AnimationClip.findByName(animations, "looking"),
        group.current
      ),
    }
    actions.current.idle.play()
    return () => {
      animations.forEach(clip => mixer.uncacheClip(clip))
    }
  }, [])

  useFrame((state, delta) => {
    mixer.update(delta)
  })

  useEffect(() => {
    if (inView && !isAnimating.current) {
      playModifierAnimation(
        actions.current.idle,
        0.25,
        actions.current.landing,
        0.25,
        isAnimating
      )
    }
  }, [inView])

  useEffect(() => {
    if (isLinkHovered && !isAnimating.current) {
      playModifierAnimation(
        actions.current.idle,
        0.25,
        actions.current.looking,
        0.25,
        isAnimating
      )
    }
  }, [isLinkHovered])

  return (
    <group ref={group} dispose={null}>
      <group
        position={[0, -1, 2]}
        rotation={[Math.PI / 2, 0, 0]}
        scale={[0.0025, 0.0025, 0.0025]}
      >
        <primitive object={nodes["mixamorigHips"]} />
        <skinnedMesh
          geometry={nodes["Neeraj"].geometry}
          skeleton={nodes["Neeraj"].skeleton}
          rotation={[-Math.PI / 2, 0, 0]}
          onClick={() => {
            if (!isAnimating.current) {
              playModifierAnimation(
                actions.current.idle,
                0.25,
                actions.current.wave,
                0.25,
                isAnimating
              )
            }
          }}
          scale={[100, 100, 100]}
        >
          <meshPhongMaterial
            attach="material"
            map={texture}
            map-flipY={false}
            skinning
          />
        </skinnedMesh>
      </group>
    </group>
  )
}
